<template>
  <CostAllocation
    v-model="shipmentRateAllocation"
    v-model:table-settings="tableSettings"
    :shipment-line-items="billingShipmentLineItems"
    :shipment-financials="shipmentFinancials"
    :loading="loading"
    @discrepancy-cost-allocation="emit('discrepancy-cost-allocation', $event)"
    @recalculate-cost-allocation="recalculateShipmentRateCostAllocation"
  />
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, ref, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { CostAllocationTableSettings } from '@rygen/rygen-ui/model/costAllocation';
import { ShipmentFinancials } from '@rygen/rygen-ui/model/shipmentFinancials';

import api from '@/api';
import { Shipment } from '@/model';
import {
  BillingShipmentLineItem,
  ShipmentRateAllocation,
} from '@/model/shipmentFinancials';

import CostAllocation from '@rygen/rygen-ui/components/costAllocation/CostAllocation.vue';

const tableSettings = defineModel({
  type: CostAllocationTableSettings,
  default: new CostAllocationTableSettings(),
});

const props = defineProps({
  shipment: {
    type: Object as PropType<Shipment>,
    default: () => new Shipment(),
  },
  shipmentFinancials: {
    type: Object as PropType<ShipmentFinancials>,
    required: true,
  },
  hasReadOnlyPermission: {
    type: Boolean,
    default: false,
  },
});
const { shipment, shipmentFinancials, hasReadOnlyPermission } = toRefs(props);

const { t } = useI18n();
const toast = useToast();

const emit = defineEmits<{
  (event: 'discrepancy-cost-allocation', hasDiscrepancy: boolean): void;
}>();

const shipmentRateAllocation: Ref<ShipmentRateAllocation> = ref(
  new ShipmentRateAllocation(),
);
const loading = ref(false);

const billingShipmentLineItems = computed((): BillingShipmentLineItem[] =>
  shipment.value.lineItems.map((lineItem) =>
    lineItem.asBillingShipmentLineItemReference(),
  ),
);

const configureCostAllocationTableSetting = () => {
  tableSettings.value.costEstimate =
    shipmentRateAllocation.value.isCostEstimateInvoiceTypes;

  if (hasReadOnlyPermission.value) {
    tableSettings.value.readOnly = true;
    tableSettings.value.readOnlyReason = t(
      'costAllocation.readOnly.contactAdmin',
    );
  } else if (
    tableSettings.value.costEstimate &&
    shipment.value.lineItems.length === 1
  ) {
    tableSettings.value.readOnly = true;
    tableSettings.value.readOnlyReason = t(
      'costAllocation.readOnly.singleShipmentLineItem',
    );
  } else if (!tableSettings.value.costEstimate) {
    tableSettings.value.readOnly = true;

    const originalInvoice = shipmentFinancials.value.invoices
      ?.filter((shipmentInvoice) => shipmentInvoice.type === 'ORIGINAL')
      ?.at(0);
    if (
      originalInvoice &&
      (originalInvoice.status === 'BILLED' ||
        originalInvoice.status === 'COMPLETE')
    ) {
      tableSettings.value.readOnlyReason = t(
        'costAllocation.readOnly.shipmentInvoiced',
      );
    } else {
      tableSettings.value.readOnlyReason = t(
        'costAllocation.readOnly.invoiceAuditComplete',
      );
    }
  } else {
    tableSettings.value.readOnly = false;
    tableSettings.value.readOnlyReason = '';
  }

  tableSettings.value.billingCurrencyUnit =
    shipmentFinancials.value.billingCurrency;

  tableSettings.value.loading = false;
};

const getShipmentRateCostAllocation = async () => {
  if (!shipment.value.id) {
    console.error('Shipment ID required to fetch cost allocation');
    return;
  }

  try {
    tableSettings.value.loading = true;

    shipmentRateAllocation.value =
      await api.shipmentFinancials.getShipmentRateAllocation(shipment.value.id);

    configureCostAllocationTableSetting();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('costAllocation.costAllocation'),
      }),
      error: e,
    });
    console.error(e);
  }
};

const recalculateShipmentRateCostAllocation = async () => {
  if (!shipment.value.id) {
    console.error('Shipment ID required to recalculate cost allocation');
    return;
  }

  try {
    loading.value = true;

    shipmentRateAllocation.value =
      await api.shipmentFinancials.recalculateShipmentRateAllocation(
        shipment.value.id,
        shipmentRateAllocation.value,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.recalculatingParam', {
        param: t('costAllocation.costAllocation'),
      }),
      error: e,
    });
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const saveShipmentRateCostAllocation = async (): Promise<boolean> => {
  if (!shipment.value.id) {
    console.error('Shipment ID required to save cost allocation');
    return false;
  }

  try {
    loading.value = true;

    await api.shipmentFinancials.updateShipmentRateAllocation(
      shipment.value.id,
      shipmentRateAllocation.value,
    );

    toast.addSuccess({
      detail: t('messages.successSavingParam', {
        param: t('costAllocation.costAllocation'),
      }),
    });

    return true;
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    toast.addError({
      detail: t('errors.savingParam', {
        param: t('costAllocation.costAllocation'),
      }),
      error: e,
    });
    console.error(e);
  } finally {
    loading.value = false;
  }

  return false;
};

defineExpose({
  getRateCostAllocation: getShipmentRateCostAllocation,
  saveRateCostAllocation: saveShipmentRateCostAllocation,
});
</script>
